/**
 * @notes: Route configurations. These are the routes used in the application. This will be used in the sidebar.
 * But in general will provide an easier access for what routes are in the application and can thus be used where needed
 */

import { STEP_TYPE } from "fieldpro-tools";
import _ from "lodash";
import { orderBy as naturalOrderBy } from "natural-orderby";

import {
  ISidebarCategory,
  TOnSubCategoryClick,
} from "components/Sidebar/Sidebar";
import { isClientBillingPlan } from "containers/authentication/utils/clientStatus";
import * as placeActions from "containers/customers/redux/actions";
import getDisplayedFolders from "containers/dashboards/utils/getDisplayedFolders";
import * as documentActions from "containers/documents/redux/actions";
import * as environmentActions from "containers/environments/redux/actions";
import { IFolder } from "containers/environments/SubcategoryManager";
import * as teamActions from "containers/teams/redux/actions";
import * as userActions from "containers/users/redux/actions/userActions";
import { OPTIMETRIKS_ROLES } from "model/application/ActionCode";
import { IOption } from "model/application/components";
import TLang from "model/application/Lang";
import { CUSTOMERS_ROUTE } from "model/constants/routes";
import { IClient, IFolder as IAppFolder } from "model/entities/Client";
import { IDashboard } from "model/entities/Dashboard";
import { IList } from "model/entities/List";
import { ISignedInUser } from "model/entities/User";
import { userCanViewJobs } from "utils/permissions/userCanViewJobs";

import {
  getDashboardsSubcategory,
  getListsSubcategory,
  getWorkflowsSubcategory,
} from "./getSubcategory";
import { ALL_ROUTES, checkRequiredActions, TRouteDefinition } from "./routes";

interface IRoute {
  path: string;
  title: string;
  icon: string;
  subCategorySelected?: string;
  subCategories?: ISidebarCategory[];
  onSubCategoryClick?: TOnSubCategoryClick;
}

interface IGetRoutes {
  actionsAvailable: Record<string, boolean>;
  client: IClient;
  lang: TLang;
  currentUser?: ISignedInUser;
  customerListName?: string;
}
interface ISelectedSubCategory {
  usermgt: string;
  teams: string;
  places: string;
  documents: string;
  environment: string;
}

export const getRoutes = ({
  actionsAvailable,
  client,
  lang,
  currentUser,
  customerListName,
}: IGetRoutes) => {
  if (!actionsAvailable) {
    return [];
  }
  const filterRoute = (route: TRouteDefinition) => {
    if (!checkRequiredActions(actionsAvailable, route.requiredActions)) {
      return false;
    }
    if (!route.extraCondition) {
      return true;
    }
    return route.extraCondition({
      client,
      user: currentUser,
      lang,
    });
  };

  const formatRoute = (route: TRouteDefinition) => {
    let title = route.title(lang);
    if (route.path === CUSTOMERS_ROUTE) {
      title = customerListName ?? title;
    }
    return {
      path: route.path,
      icon: route.icon,
      title,
    };
  };

  return _(ALL_ROUTES).filter(filterRoute).map(formatRoute).value();
};

export const getSelectedCategory = (
  location: Location,
  selectedSubCategory: ISelectedSubCategory,
  routes: IRoute[],
): IRoute[] => {
  const newRoutes = _.map(routes, (route) => {
    switch (route.icon) {
      case "home":
      case "supervisor_account":
      case "erp":
      case "panorama":
      case "photo_camera":
      case "calendar": {
        return {
          ...route,
          subCategorySelected: "index",
        };
      }
      case "dashboard": {
        return {
          ...route,
          subCategorySelected: getDashboardsSubcategory(location?.pathname),
        };
      }
      case "person": {
        return {
          ...route,
          subCategorySelected: selectedSubCategory.usermgt,
        };
      }
      case "group_work": {
        return {
          ...route,
          subCategorySelected: selectedSubCategory.teams,
        };
      }
      case "work": {
        return {
          ...route,
          subCategorySelected: getWorkflowsSubcategory(location?.pathname),
        };
      }
      case "list":
      case "customers": {
        return {
          ...route,
          subCategorySelected: getListsSubcategory(location?.pathname),
        };
      }
      case "place": {
        return {
          ...route,
          subCategorySelected: selectedSubCategory.places,
        };
      }
      case "assignment": {
        return {
          ...route,
          subCategorySelected: selectedSubCategory.documents,
        };
      }
      case "environment": {
        return {
          ...route,
          subCategorySelected: selectedSubCategory.environment,
        };
      }
      default: {
        return route;
      }
    }
  });

  return newRoutes;
};

export const ERP_LIST_IDS = [
  "customerpricegroup",
  "customerpromogroup",
  "packaging",
  "price",
  "stock",
  "promotionads",
  "reward",
  "storage",
];

export const getSubCategories = (
  routes: IRoute[],
  lang: TLang,
  dashboards: IDashboard[],
  levels: IOption[],
  environmentFolders: IFolder[],
  client: IClient,
  lists: IList[],
  dashboardFolders: IAppFolder[],
  currentRole: string,
  actionsAvailable: Record<string, boolean>,
): IRoute[] => {
  const folderIds = _.uniq(
    dashboards
      .filter((d) => d.active)
      .filter((d) => ["WEB", "MOBILE_AND_WEB"].includes(d.type))
      .map((d) => d.folder || "default"),
  );
  const fullFolders = _.filter(
    dashboardFolders,
    (f) => folderIds.includes(f.id) || folderIds.includes(f.name),
  );

  const displayedFolders = getDisplayedFolders({
    folders: fullFolders,
    client,
  });

  const sortedFullFolders = naturalOrderBy(displayedFolders, "name", "asc");
  let assignmentSubCategories;
  const subCategoriesWork: ISidebarCategory[] = [
    {
      name: lang.mainLayout.sidebar.summary,
      id: "index",
    },
    {
      name: lang.mainLayout.sidebar.activities,
      id: "activities",
    },
  ];

  const updatedRoutes = routes.map((route) => {
    switch (route.icon) {
      case "calendar":
      case "home": {
        return {
          ...route,
          subCategories: [],
        };
      }
      case "dashboard": {
        return {
          ...route,
          subCategories: [
            {
              name: lang.mainLayout.sidebar.summary,
              id: "index",
            },
            ...sortedFullFolders
              .filter((f) => f.active)
              .map((f) => ({ name: f.name, id: f.id })),
          ],
        };
      }
      case "person": {
        return {
          ...route,
          subCategories: [
            {
              name: lang.mainLayout.sidebar.mobileUsers,
              id: "mobileuser",
            },
            {
              name: lang.mainLayout.sidebar.webUsers,
              id: "webuser",
            },
          ],
        };
      }
      case "group_work": {
        return {
          ...route,
          subCategories: [
            {
              name: lang.mainLayout.sidebar.teams,
              id: "team",
            },
            ...levels.slice(1).map((l) => ({ name: l.label, id: l.key })),
          ],
        };
      }
      case "work": {
        if (!isClientBillingPlan(client, "starter")) {
          subCategoriesWork.push({
            name: lang.mainLayout.sidebar.jobs,
            id: "jobs",
          });
          if (
            userCanViewJobs({
              jobType: STEP_TYPE.TRIGGER,
              availableActions: actionsAvailable,
              availableJobs: client.available_jobs,
              role: currentRole,
            })
          ) {
            subCategoriesWork.push({
              name: lang.mainLayout.sidebar.triggers,
              id: "triggers",
            });
          }
          if (
            userCanViewJobs({
              jobType: STEP_TYPE.WEBHOOK,
              availableActions: actionsAvailable,
              availableJobs: client.available_jobs,
              role: currentRole,
            })
          ) {
            subCategoriesWork.push({
              name: lang.mainLayout.sidebar.webhooks,
              id: "webhooks",
            });
          }
        }
        return {
          ...route,
          subCategories: subCategoriesWork,
        };
      }
      case "list": {
        return {
          ...route,
          subCategories: [
            {
              name: lang.mainLayout.sidebar.summary,
              id: "index",
            },
            ...lists
              .filter(
                (l) =>
                  l.active &&
                  !["territories", "customer"]
                    .concat(ERP_LIST_IDS)
                    .includes(l.id),
              )
              .map((l) => ({ name: l.name, id: l.id })),
          ],
        };
      }
      case "customers": {
        return {
          ...route,
          subCategories: [
            {
              name: lang.mainLayout.sidebar.customers,
              id: "index",
            },
          ],
        };
      }
      case "place": {
        return {
          ...route,
          subCategories: [
            {
              name: lang.mainLayout.sidebar.map,
              id: "map",
            },
          ],
        };
      }
      case "erp": {
        return {
          ...route,
          subCategories: _.sortBy(
            [
              {
                name: lang.mainLayout.sidebar.orders,
                id: "orders",
              },
              {
                name: "Stock (old)",
                id: "old_stock",
              },
              {
                name: "Stock movements",
                id: "stock_movements",
              },
              {
                name: "Discounts",
                id: "discounts",
              },
              ..._.filter(lists, (list) =>
                _.includes(
                  ["sku", "territories"].concat(ERP_LIST_IDS),
                  list.id,
                ),
              ),
            ],
            "name",
          ),
        };
      }
      case "panorama": {
        return {
          ...route,
          subCategories: [
            {
              name: lang.mainLayout.sidebar.orders,
              id: "orders",
            },
            // Temporary sub-menus for a demo
            {
              name: "Customers",
              id: "customers",
            },
            {
              name: "Customer Groups",
              id: "customer_groups",
            },
            {
              name: "Cust. Balances",
              id: "balances",
            },
            {
              name: "Products",
              id: "products",
            },
            {
              name: "Product Groups",
              id: "product_groups",
            },
            {
              name: "Prices",
              id: "prices",
            },
            {
              name: "Discounts",
              id: "discounts",
            },
            {
              name: "Order Lines",
              id: "lines",
            },
            {
              name: "Stock",
              id: "stock",
            },
            {
              name: "S.R.E.",
              id: "recommendations",
            },
            {
              name: "Distributors",
              id: "distributors",
            },
            {
              name: "Warehouses",
              id: "warehouses",
            },
          ],
        };
      }
      case "assignment": {
        assignmentSubCategories = [
          {
            name: lang.mainLayout.sidebar.clients,
            id: "index",
          },
          {
            name: "Optimetriks",
            id: "optimetriks",
          },
        ];
        return {
          ...route,
          subCategories: [
            OPTIMETRIKS_ROLES.SUPERADMIN as string,
            OPTIMETRIKS_ROLES.ADMIN as string,
          ].includes(currentRole)
            ? assignmentSubCategories
            : [{ name: lang.mainLayout.sidebar.clients, id: "index" }],
        };
      }
      case "environment": {
        return {
          ...route,
          subCategories: environmentFolders,
        };
      }
      case "supervisor_account":
      case "photo_camera": {
        return {
          ...route,
          subCategories: [
            {
              name: lang.mainLayout.sidebar.summary,
              id: "index",
            },
          ],
        };
      }
      default: {
        return { ...route, subCategories: [] };
      }
    }
  });

  return updatedRoutes;
};

type PlaceActionsType = typeof placeActions;

export const getOnSubCategoryClick = (
  routes: IRoute[],
  userActions: userActions.IUserActions,
  documentActions: documentActions.IDocumentsActions,
  teamActions: teamActions.ITeamsActions,
  environmentActions: environmentActions.IEnvironmentActions,
  placeActions: PlaceActionsType,
): IRoute[] => {
  const updatedRoutes = routes.map((route) => {
    switch (route.icon) {
      case "home":
      case "dashboard":
      case "work":
      case "list":
      case "panorama":
      case "photo_camera":
      case "supervisor_account":
      case "erp":
      case "calendar":
      case "customers":
        return {
          ...route,
          onSubCategoryClick: () => {},
        };

      case "person":
        return {
          ...route,
          onSubCategoryClick:
            userActions.changeSubcategoryAction as TOnSubCategoryClick,
        };
      case "group_work":
        return {
          ...route,
          onSubCategoryClick: teamActions.changeSubcategoryAction,
        };
      case "place":
        return {
          ...route,
          onSubCategoryClick: placeActions.changeSubcategoryAction,
        };
      case "assignment":
        return {
          ...route,
          onSubCategoryClick: documentActions.changeSubcategoryAction,
        };
      case "environment":
        return {
          ...route,
          onSubCategoryClick: environmentActions.changeSubcategoryAction,
        };
      default:
        return route;
    }
  });

  return updatedRoutes;
};
